import { createRouter, createWebHistory } from 'vue-router';
import MapPage from '@/map/MapPage.vue';
import HomePage from '@/home/HomePage.vue';
import ProductsPage from '@/products/ProductsPage.vue';
import VehiclesPage from '@/vehicles/VehiclesPage.vue';
import Login from './auth/Login.vue';
import Logout from './auth/Logout.vue';
import { useAuthStore } from './auth/store/authStore';
import { useSelectedSiteStore } from './sites/stores/selectedSiteStore';
import StartPage from './auth/StartPage.vue';
import StylesPage from './mapStyles/MapStylesPage.vue';
import CoilsPage from './coils/CoilsPage.vue';
import SystemSettingsPage from './settings/SystemSettingsPage.vue';
import { useFeatureFlagStore } from './featureFlags/stores/featureFlagStore';
import { DefinedFeatureFlags } from 'shared/featureFlags/domain/definedFeatureFlags';
import DashboardsPage from './dashboards/DashboardsPage.vue';
import EditDashboardPage from './dashboards/EditDashboardPage.vue';
import ReleaseNotePage from './releaseNotes/ReleaseNotePage.vue';
import ProductionOrdersPage from './productionOrders/ProductionOrdersPage.vue';
import EditProductionOrderPage from './productionOrders/EditProductionOrderPage.vue';
import ReportPage from './reports/ReportPage.vue';
import PackagesPage from './packages/PackagesPage.vue';
import { useThemeStore } from 'shared/themes/stores/themeStore';
import DeliveriesPage from './deliveries/DeliveriesPage.vue';
import InboundDeliveryPage from './deliveries/InboundDeliveryPage.vue';
import OutboundDeliveryPage from './deliveries/OutboundDeliveryPage.vue';

// Export a function that creates the router with dynamic base path
export default function createAppRouter(basePath: string = '') {
  const router = createRouter({
    history: createWebHistory(basePath),
    routes: [
      {
        path: '/',
        name: 'start',
        component: StartPage,
      },
      {
        path: '/home',
        name: 'home',
        component: HomePage,
      },
      {
        path: '/map',
        name: 'map',
        component: MapPage,
      },
      {
        path: '/products',
        name: 'products',
        component: ProductsPage,
      },
      {
        path: '/productGroups',
        name: 'productGroups',
        redirect: {
          name: 'products',
          query: {
            tab: 'productGroups',
          },
        },
      },
      {
        path: '/vehicles',
        name: 'vehicles',
        component: VehiclesPage,
      },
      {
        path: '/styles',
        name: 'styles',
        component: StylesPage,
      },
      {
        path: '/coils',
        name: 'coils',
        component: CoilsPage,
      },
      {
        path: '/systemsettings',
        name: 'systemsettings',
        component: SystemSettingsPage,
      },
      {
        path: '/login',
        name: 'login',
        component: Login,
      },
      {
        path: '/logout',
        name: 'logout',
        component: Logout,
      },
      {
        path: '/dashboards',
        name: 'dashboards',
        component: DashboardsPage,
      },
      {
        path: '/dashboard',
        name: 'createDashboard',
        component: EditDashboardPage,
      },
      {
        path: '/dashboard/:id',
        name: 'editDashboard',
        component: EditDashboardPage,
      },
      {
        path: '/releaseNotes',
        name: 'releaseNotes',
        component: ReleaseNotePage,
      },
      {
        path: '/productionorders',
        name: 'productionOrders',
        component: ProductionOrdersPage,
      },
      {
        path: '/productionorder',
        name: 'createProductionOrder',
        component: EditProductionOrderPage,
      },
      {
        path: '/productionorder/:id',
        name: 'editProductionOrder',
        component: EditProductionOrderPage,
      },
      {
        path: '/reports',
        name: 'reports',
        // Lazy load the reports module
        component: ReportPage,
      },
      {
        path: '/packages',
        name: 'packages',
        component: PackagesPage,
      },
      {
        path: '/packaging',
        name: 'packaging',
        redirect: {
          name: 'packages',
          query: {
            tab: 'packaging',
          },
        },
      },
      {
        path: '/package-types',
        name: 'packageTypes',
        redirect: {
          name: 'packages',
          query: {
            tab: 'packageTypes',
          },
        },
      },
      {
        path: '/mapping-profile',
        name: 'mappingProfile',
        redirect: () =>
          useFeatureFlagStore().isFeatureEnabled(DefinedFeatureFlags.Packages)
            ? { name: 'products', query: { tab: 'mappingProfile' } }
            : { name: 'products' },
      },
      {
        path: '/mapping-trace',
        name: 'mappingTrace',
        redirect: () =>
          useFeatureFlagStore().isFeatureEnabled(DefinedFeatureFlags.Packages)
            ? { name: 'products', query: { tab: 'mappingTrace' } }
            : { name: 'products' },
      },
      {
        path: '/delivery',
        name: 'delivery',
        component: DeliveriesPage,
      },
      {
        path: '/delivery/inbound',
        name: 'createInboundDelivery',
        component: InboundDeliveryPage,
      },
      {
        path: '/delivery/inbound/:id',
        name: 'editInboundDelivery',
        component: InboundDeliveryPage,
      },
      {
        path: '/delivery/outbound',
        name: 'createOutboundDelivery',
        component: OutboundDeliveryPage,
      },
      {
        path: '/delivery/outbound/:id',
        name: 'editOutboundDelivery',
        component: OutboundDeliveryPage,
      },
    ],
  });

  router.beforeEach(async (to, from, next) => {
    const theme = useThemeStore();
    const documentTitle = theme.currentTheme == 'SILog' ? 'SILog Cloud' : 'TiYard Cloud';
    document.title = documentTitle;

    const auth = useAuthStore();
    const siteStore = useSelectedSiteStore();
    const featureFlagStore = useFeatureFlagStore();

    await auth.initialize();

    if (to.name === 'login') {
      if (auth.isAuthenticated) {
        next({ name: 'start' });
      } else {
        next();
      }
      return;
    }

    if (to.name === 'logout') {
      if (auth.isAuthenticated) {
        next();
      } else {
        next({ name: 'start' });
      }
      return;
    }

    if (auth.isAuthenticated) {
      await siteStore.restore();
      await featureFlagStore.initialize();

      if (to.name === 'start') {
        next({ name: 'home' });
      } else {
        next();
      }
      return;
    }

    if (to.name === 'start') {
      next();
      return;
    }

    next({ name: 'start' });
  });

  return router;
}
